import { CommonModule } from '@angular/common'
import { Component, Inject, Injectable } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import {
  MAT_SNACK_BAR_DATA,
  MatSnackBar,
  MatSnackBarConfig,
  MatSnackBarRef,
  SimpleSnackBar,
} from '@angular/material/snack-bar'
import { get, isNil, isString, without } from 'lodash'
import { BehaviorSubject } from 'rxjs'

import { toSafeUiMessage } from '../utils/ui-message'
import { PatientPalRestError } from './rest'

export interface SnackBarOptions {
  message: string
  panelClass?: 'success' | 'warn' | 'error'
  duration?: null | number
  action?: string
  config?: MatSnackBarConfig
}

@Component({
  selector: 'mmx-safe-snack',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
  ],
  template: `
    <div style="display: flex; justify-content: space-between; align-items: center;">
      <span>{{ data.message }}</span>
      <button mat-button color="primary" *ngIf="data.action" type="button" (click)="dismiss()">
        {{ data.action }}
      </button>
    </div>
  `,
})
class SafeSnackComponent {
  constructor(
    @Inject(MAT_SNACK_BAR_DATA) public data: { message: string; action?: string },
    private snackRef: MatSnackBarRef<SafeSnackComponent>,
  ) {}

  dismiss() {
    this.snackRef.dismiss()
  }
}

export class AsyncProcess {
  id: number
  snackRef: MatSnackBarRef<SimpleSnackBar>
  inProgress = false
  timeout = 10000
  timer: number // NodeJS.Timer

  constructor(private progressService: ProgressIndicatorService) {
    this.id = Date.now()
  }

  start(): AsyncProcess {
    this.inProgress = true
    this.progressService.start(this)
    this.timer = setTimeout(() => this.stop(), this.timeout) as any
    return this
  }

  stop(snackBarOptions?: string | SnackBarOptions): AsyncProcess {
    clearTimeout(this.timer)
    this.inProgress = false
    this.progressService.stop(this)

    if (snackBarOptions) {
      this.progressService.snack(snackBarOptions)
    }

    return this
  }

  fail(message: string): AsyncProcess {
    clearTimeout(this.timer)
    this.inProgress = false
    this.progressService.stop(this)
    this.progressService.snack({
      message,
      action: 'Dismiss',
      config: {
        duration: undefined,
        panelClass: 'error',
      },
    })

    return this
  }
}

@Injectable()
export class ProgressIndicatorService {
  // public enabled: Observable<boolean>
  public subject: BehaviorSubject<boolean>
  public catchError: (err: PatientPalRestError) => void

  // private _enabled = false
  private requests: AsyncProcess[] = []

  constructor(private snackBar: MatSnackBar) {
    // this.enabled = this.subject.asObservable()
    this.subject = new BehaviorSubject<boolean>(false)

    this.catchError = (err: PatientPalRestError) => {
      this.error(err)
    }
  }

  error(err: PatientPalRestError) {
    const errorMessage =
      get(err, 'error.errors[0].message') ||
      err.error.message ||
      'An error occurred. Please try again.'
    this.snack({
      message: errorMessage,
      panelClass: 'warn',
    })
  }

  async(): AsyncProcess {
    return new AsyncProcess(this)
  }

  start(process?: AsyncProcess): AsyncProcess {
    if (isNil(process)) {
      process = this.async()
    }

    this.requests.push(process)

    // if (!this._enabled) {
    this.subject.next(true)
    // this._enabled = true
    // }

    return process
  }

  stop(process: AsyncProcess): AsyncProcess {
    this.requests = without(this.requests, process)

    if (this.requests.length === 0) {
      this.subject.next(false)
      // this._enabled = false
    }

    return process
  }

  snack(snackBarOptions: SnackBarOptions | string) {
    let options: SnackBarOptions

    if (isString(snackBarOptions)) {
      options = {
        message: snackBarOptions as string,
      }
    } else {
      options = snackBarOptions as SnackBarOptions
    }

    const safeMessage = toSafeUiMessage({ message: options.message })
    const allowedActions = new Set(['Close', 'Dismiss', 'OK', 'Retry'])
    const safeAction =
      options.action && allowedActions.has(options.action) ? options.action : 'Close'

    let duration = 10000

    if (options.panelClass === 'warn' && options.duration == null) {
      duration = undefined
    } else if (options.duration != null) {
      duration = options.duration
    }

    const allowedPanelClasses = new Set(['success', 'warn', 'error'])
    const safePanelClass =
      typeof options.panelClass === 'string' && allowedPanelClasses.has(options.panelClass)
        ? options.panelClass
        : undefined

    const safeConfig: MatSnackBarConfig = {
      duration,
      horizontalPosition: 'center', // 'start' | 'center' | 'end' | 'left' | 'right'
      verticalPosition: 'top', // 'top' | 'bottom'
      panelClass: safePanelClass,
      ...this.pickSafeConfig(options.config),
    }

    const config: MatSnackBarConfig = {
      ...safeConfig,
      data: { message: safeMessage, action: safeAction },
    }
    this.snackBar.openFromComponent(SafeSnackComponent, config)
  }

  private pickSafeConfig(config?: MatSnackBarConfig): MatSnackBarConfig {
    if (!config) {
      return {}
    }

    const safe: MatSnackBarConfig = {}
    if (config.horizontalPosition) safe.horizontalPosition = config.horizontalPosition
    if (config.verticalPosition) safe.verticalPosition = config.verticalPosition
    if (config.politeness) safe.politeness = config.politeness
    if (config.announcementMessage) safe.announcementMessage = config.announcementMessage
    if (config.direction) safe.direction = config.direction

    return safe
  }

  // wrap<T>(observable: Observable<T>): Subject<T> {
  //   this.enabled = true

  //   const shared = observable.pipe(share())

  //   shared.subscribe(
  //     (resp) => {
  //       this.enabled = false
  //     },
  //     (err) => {
  //       this.enabled = false
  //     },
  //   )

  //   return subject
  // }
}
